// @ts-nocheck
import React from 'react';
import classNames from 'classnames';
import {
  LuCpu,
  LuHardDrive,
  LuServer,
  LuPackage,
  LuNetwork,
} from 'react-icons/lu';
import { FiHash } from 'react-icons/fi';

import {
  SMART_COOLER_STORAGE_COLORS,
  getStorageColorTier,
  VIEW_SC_STRINGS,
} from '@/constant/smartCooler/smartCooler.constant';
import { APP_BG, APP_BORDER, APP_TEXT } from '@/constant/theme/colors.constant';
import { getDateTime } from '@/utils/dateTime';
import { getLabelIcon } from '@/components/atoms/IconMap';
import { Text } from '@/components/atoms/AppText';
import { SectionCard } from '@/components/organism/Cards/SectionCard';
import { StatCell } from '@/components/organism/Cards/StatCell';
import { StorageRing } from '@/components/atoms/StorageRing';
import { ISmartCooler } from '../smartCooler.interface';

interface Props {
  item: ISmartCooler;
  sysInfo: Record<string, any>;
  hasSys: boolean;
  storageUsed: number | null;
}

export const ViewSCSystemTab: React.FC<Props> = ({
  item,
  sysInfo,
  hasSys,
  storageUsed,
}) => {
  if (!hasSys) {
    return (
      <div
        className={classNames(
          'flex flex-col items-center justify-center gap-4 rounded-2xl border py-20',
          APP_BORDER.soft,
          APP_BG.surface,
        )}
      >
        <div
          className={classNames(
            'flex h-14 w-14 items-center justify-center rounded-2xl',
            APP_BG.muted,
          )}
        >
          <LuCpu className="w-7 h-7 text-gray-300 dark:text-gray-600" />
        </div>
        <div className="text-center space-y-1">
          <Text as="p" size="base" weight="semibold" color="subtle">
            {VIEW_SC_STRINGS.SYS_NO_DATA_TITLE}
          </Text>
          <Text as="p" size="sm" color="muted">
            {VIEW_SC_STRINGS.SYS_NO_DATA_DESC}
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols gap-4">
      {/* Software */}
      <SectionCard
        title={VIEW_SC_STRINGS.SECTION_SOFTWARE_TITLE}
        icon={<LuPackage className="w-4 h-4" />}
        subtitle={VIEW_SC_STRINGS.SECTION_SOFTWARE_SUB}
        bodyClassName=""
      >
        <div
          className={classNames(
            'grid grid-cols-2 sm:grid-cols-3 divide-x divide-y',
            APP_BORDER.divideSoft,
          )}
        >
          <StatCell
            size="md"
            labelIcon={getLabelIcon(VIEW_SC_STRINGS.LABEL_APP_VERSION)}
            label={VIEW_SC_STRINGS.LABEL_APP_VERSION}
            value={sysInfo.appVersion}
            mono
          />
          <StatCell
            size="md"
            labelIcon={getLabelIcon(VIEW_SC_STRINGS.LABEL_OS_VERSION)}
            label={VIEW_SC_STRINGS.LABEL_OS_VERSION}
            value={sysInfo.osVersion}
            mono
          />
          <StatCell
            size="md"
            labelIcon={<LuNetwork className="w-3 h-3" />}
            label={VIEW_SC_STRINGS.LABEL_NETWORK}
            value={sysInfo.networkType}
          />
          <StatCell
            size="md"
            labelIcon={getLabelIcon(VIEW_SC_STRINGS.LABEL_TIMEZONE)}
            label={VIEW_SC_STRINGS.LABEL_TIMEZONE}
            value={sysInfo.timezone}
          />
          <StatCell
            size="md"
            labelIcon={getLabelIcon(VIEW_SC_STRINGS.LABEL_SYSTEM_TIME)}
            label={VIEW_SC_STRINGS.LABEL_SYSTEM_TIME}
            value={getDateTime(sysInfo.systemDtm ?? '')}
          />
          <StatCell
            size="md"
            labelIcon={getLabelIcon(VIEW_SC_STRINGS.LABEL_LAST_UPDATED)}
            label={VIEW_SC_STRINGS.LABEL_LAST_UPDATED}
            value={getDateTime(sysInfo.lastUpdatedDtm ?? '')}
          />
        </div>
      </SectionCard>

      {/* Disk usage */}
      {storageUsed !== null && (
        <SectionCard
          title={VIEW_SC_STRINGS.SECTION_DISK_TITLE}
          icon={<LuHardDrive className="w-4 h-4" />}
          subtitle={VIEW_SC_STRINGS.SECTION_DISK_SUB}
        >
          <div className="flex flex-col sm:flex-row items-center gap-6 p-1">
            <StorageRing pct={storageUsed} size={96} />
            <div className="flex flex-1 min-w-0 w-full flex-col gap-3">
              <div className="flex items-center justify-between">
                <Text
                  as="span"
                  size="xs"
                  color="subtle"
                  className="uppercase tracking-widest"
                >
                  {VIEW_SC_STRINGS.SYS_USAGE_LABEL}
                </Text>
                <Text
                  as="span"
                  size="xs"
                  weight="bold"
                  className={getStorageColorTier(storageUsed).text}
                >
                  {storageUsed}
                  {VIEW_SC_STRINGS.SYS_USED_SUFFIX}
                </Text>
              </div>
              <div
                className={classNames(
                  'h-2.5 w-full rounded-full overflow-hidden',
                  APP_BG.muted,
                )}
              >
                <div
                  className={classNames(
                    'h-full rounded-full transition-all duration-700',
                    (getStorageColorTier(storageUsed) as any).bar ?? '',
                  )}
                  style={{ width: `${storageUsed}%` }}
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  {
                    l: VIEW_SC_STRINGS.SYS_STAT_USED,
                    v: `${sysInfo.totalStorageMb - sysInfo.availableStorageMb} ${VIEW_SC_STRINGS.SYS_MB_SUFFIX}`,
                    c: APP_TEXT.soft,
                  },
                  {
                    l: VIEW_SC_STRINGS.SYS_STAT_FREE,
                    v: `${sysInfo.availableStorageMb} ${VIEW_SC_STRINGS.SYS_MB_SUFFIX}`,
                    c: SMART_COOLER_STORAGE_COLORS.healthy.text,
                  },
                  {
                    l: VIEW_SC_STRINGS.SYS_STAT_TOTAL,
                    v: `${sysInfo.totalStorageMb} ${VIEW_SC_STRINGS.SYS_MB_SUFFIX}`,
                    c: APP_TEXT.soft,
                  },
                ].map((s) => (
                  <div
                    key={s.l}
                    className={classNames(
                      'rounded-xl border p-2.5 text-center',
                      APP_BORDER.soft,
                      APP_BG.surface,
                    )}
                  >
                    <Text as="p" size="xs" color="muted" className="mb-0.5">
                      {s.l}
                    </Text>
                    <Text
                      as="p"
                      size="sm"
                      weight="bold"
                      className={classNames('font-mono', s.c)}
                    >
                      {s.v}
                    </Text>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </SectionCard>
      )}

      {/* Pod info */}
      {item.meta?.podInfo && (
        <SectionCard
          title={VIEW_SC_STRINGS.SECTION_POD_TITLE}
          icon={<LuServer className="w-4 h-4" />}
          subtitle={VIEW_SC_STRINGS.SECTION_POD_SUB}
          bodyClassName=""
        >
          <div
            className={classNames(
              'grid grid-cols-1 sm:grid-cols-2 divide-x divide-y',
              APP_BORDER.divideSoft,
            )}
          >
            <StatCell
              size="md"
              labelIcon={<FiHash className="w-3 h-3" />}
              label={VIEW_SC_STRINGS.LABEL_POD_ID}
              value={item.meta.podInfo.podId}
              mono
            />
            <StatCell
              size="md"
              labelIcon={getLabelIcon('URL')}
              label={VIEW_SC_STRINGS.LABEL_INTERNAL_URL}
              value={item.meta.podInfo.podInternalUrl}
            />
          </div>
        </SectionCard>
      )}
    </div>
  );
};
